import React, { type ReactNode } from "react";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardBackspaceOutlinedIcon from "@mui/icons-material/KeyboardBackspaceOutlined";
import useStyles from "./MelaModalHeader.styles";
import Sizes from "../../Sizes";
import Text from "../../Text";
import Title from "../../Title";
import View from "../../View";
import { MelaButton } from "../MelaButton";

export interface MelaModalHeaderProps {
  title: ReactNode;
  onBackButton?: () => void;
  showBackButton?: boolean;
  backButtonLabel?: string;
  headerStyle?: string;
  RightButton?: ReactNode;
  titleGroupStyle?: string;
  subtitle?: string;
  subtitleStyle?: string;
  titleStyle?: string;
  titleNoMargin?: boolean;
  titleContainerStyle?: string;
  RightComponent?: ReactNode;
  showCloseButton?: boolean;
  onClose?: () => void;
  closeButtonDisabled?: boolean;
  closeButtonAriaLabel?: string;
  closeButtonDataTestId?: string;
  dataTestId?: string;
}

export const MelaModalHeader = ({
  title,
  onBackButton = undefined,
  showBackButton = false,
  backButtonLabel = "",
  headerStyle = "",
  RightButton = null,
  titleGroupStyle = "",
  titleStyle = "",
  titleContainerStyle = "",
  titleNoMargin = false,
  subtitle = undefined,
  RightComponent,
  showCloseButton = false,
  onClose,
  closeButtonDisabled = false,
  closeButtonAriaLabel = "Close",
  closeButtonDataTestId,
  subtitleStyle = "",
  dataTestId = "",
}: MelaModalHeaderProps) => {
  const styles = useStyles();
  const titleRowStyle = {
    width: "100%",
    flexDirection: "row" as const,
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  };
  const titleContentStyle = {
    flex: 1,
    minWidth: 0,
  };
  const closeButton = showCloseButton ? (
    <MelaButton
      iconOnly
      tone="neutral"
      variant="ghost"
      onClick={onClose}
      disabled={closeButtonDisabled}
      aria-label={closeButtonAriaLabel}
      dataTestId={closeButtonDataTestId}
      style={{
        minWidth: 32,
        width: 32,
        height: 32,
        padding: 0,
        marginRight: 16,
      }}
      startIcon={<CloseIcon fontSize="small" />}
    />
  ) : null;
  const rightComponent =
    RightComponent || closeButton ? (
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: 8,
          flexShrink: 0,
        }}
      >
        {RightComponent}
        {closeButton}
      </View>
    ) : null;

  return showBackButton ? (
    <>
      <View className={styles.backButtonHeader}>
        <View
          className={styles.buttonContainer}
          onClick={onBackButton}
          dataTestId="modal-header-back-button"
        >
          <KeyboardBackspaceOutlinedIcon />
          <h4>{backButtonLabel}</h4>
        </View>
        <View
          className={`${styles.titleGroup} ${titleGroupStyle}`}
          dataTestId={dataTestId}
        >
          <View className={titleContainerStyle} style={titleRowStyle}>
            <View style={titleContentStyle}>
              <Title
                className={`${titleNoMargin ? "ml-0" : "ml-4"} ${titleStyle}`}
              >
                {title}
              </Title>
            </View>
            {rightComponent}
          </View>
          {subtitle && (
            <Text
              size={Sizes.small}
              className={`${styles.subtitle} ${subtitleStyle}`}
            >
              {subtitle}
            </Text>
          )}
          {RightButton}
        </View>
      </View>
    </>
  ) : (
    <>
      <View className={headerStyle} dataTestId={dataTestId}>
        <View className={titleContainerStyle} style={titleRowStyle}>
          <View style={titleContentStyle}>
            <Title
              size={Sizes.medium}
              className={`${titleNoMargin ? "ml-0" : "ml-4"} ${titleStyle}`}
            >
              {title}
            </Title>
          </View>
          {rightComponent}
        </View>
        {subtitle && (
          <Text
            size={Sizes.small}
            className={`${styles.subtitle} ${subtitleStyle}`}
          >
            {subtitle}
          </Text>
        )}
      </View>
    </>
  );
};
